<template>
  <MarketplaceCard
    v-if="config"
    v-model="switchOn"
    :disabled="switchDisabled"
    :logo-path="imageUrl"
    logo-alt="JB Warranties"
    description="Our online system provides contractors with the most comprehensive and easy to use extended service agreement solution."
    learn-more-link="https://www.jbwarranties.com/"
    cy-selector="marketplaceJBW"
  >
    <template #configure>
      <div class="link" @click="openModal">Set Up Information</div>
    </template>
    <template #modal>
      <Modal
        v-if="modalOpen"
        title="Set Up Information - JB Warranties"
        class-name="jb-warranties-config-modal"
        no-submit
        @hide-modal="closeModal"
      >
        <!-- Dealer Onboarding Flow -->
        <div v-if="setupMode === 'dealer_id'" class="d-flex flex-column">
          <DealerOnboardingSection
            :config="config"
            :submitting="submitting"
            @request-participation="handleRequestParticipation"
          />
        </div>

        <!-- Legacy API Key Flow -->
        <div v-else class="d-flex flex-column">
          <InputField
            id="jbw-api-key"
            v-model="config.api_key"
            title="API Key"
            placeholder="Enter API Key"
            action-text="Validate"
            type="password"
            toggleable
            :loading="validating"
            :validated="validated"
            :locked="validated"
            class="mb-3"
            @action="validateApiKey"
            @unlock="unlockApiKey"
          />
          <div v-if="config.verified_at" class="mt-3">
            <hr class="border-gray-200" />
            <span class="block mb-2 mt-3 text-sm font-medium text-gray-900">
              Dealer Information (Populated from API)
            </span>
            <div class="d-flex gap-3 mb-2">
              <InputField
                :model-value="config.contact_first_name || ''"
                class="flex-1"
                title="First Name"
                :disabled="true"
              />
              <InputField
                :model-value="config.contact_last_name || ''"
                class="flex-1"
                title="Last Name"
                :disabled="true"
              />
            </div>
            <div class="d-flex gap-3 mb-2">
              <InputField
                :model-value="config.ein || ''"
                class="flex-1"
                title="EIN"
                :disabled="true"
              />
              <InputField
                :model-value="config.state_license_number || ''"
                class="flex-1"
                title="State License Number"
                :disabled="true"
              />
              <InputField
                :model-value="config.dealer_id?.toString() || ''"
                class="flex-1"
                title="Dealer ID"
                :disabled="true"
              />
            </div>
          </div>
        </div>

        <template #footer>
          <div
            class="modal-footer d-flex justify-content-between align-items-center"
          >
            <div class="d-flex gap-2">
              <Button
                v-if="showCancelButton"
                type="secondary"
                @click="closeModal()"
              >
                Cancel
              </Button>
              <Button
                v-if="
                  config.persisted &&
                  (setupMode === 'api_key' || setupMode === 'dealer_id')
                "
                type="danger"
                @click="confirmDelete()"
              >
                Delete
              </Button>
            </div>
            <Alert
              v-if="validatedUnsaved && !saving && setupMode === 'api_key'"
              :text="validationWarningText"
              type="warning"
              class="!py-2.5"
            />
            <Button
              v-if="setupMode === 'api_key'"
              :disabled="!dirty || saving"
              @click="saveIntegration"
            >
              {{ saving ? "Saving..." : "Save" }}
            </Button>
          </div>
        </template>
      </Modal>
      <Modal
        v-if="deleteConfirm"
        title="Delete Configuration"
        @hide-modal="toggleDeleteConfirm"
      >
        <div>
          This configuration will be permanently deleted and you will not be
          able to undo this action. Are you sure?
        </div>
        <template #submit>
          <Button id="submit_modal_btn" type="danger" @click="dropIntegration">
            Delete Configuration
          </Button>
        </template>
      </Modal>
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable.js";
import { EventBus } from "@/lib/event_bus";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import VueChannel from "@/channels/VueChannel";
import JBWarrantiesLogo from "@/images/jb_warranties_logo.png";
import DealerOnboardingSection from "./JBWarranties/DealerOnboardingSection.vue";
import MarketplaceCard from "./MarketplaceCard.vue";

type SetupMode = "api_key" | "dealer_id";

const store = useStore();
const isDealerOnboardingEnabled = useFeatureFlag("jbw_jb360");

const imageUrl = JBWarrantiesLogo;
const deleteConfirm = ref(false);
const switchDisabled = ref<boolean | null>(null);
const switchOn = ref<boolean | null>(null);
const modalOpen = ref(false);
const saving = ref(false);
const validating = ref(false);
const dirty = ref(false);
const submitting = ref(false);
const setupMode = ref<SetupMode>("api_key");
let channel: VueChannel | null = null; // eslint-disable-line prefer-const

const jbWarranties = computed(() => store.state.jbWarranties);
const config = computed(() => jbWarranties.value?.config);
const validated = computed(() => !!config.value?.verified_at);
const unsaved = computed(() => !config.value?.persisted || dirty.value);
const validatedUnsaved = computed(() => validated.value && unsaved.value);
const validationWarningText = "API Key validated, but not yet saved.";

const showCancelButton = computed(() => {
  if (setupMode.value === "dealer_id") return false;
  return unsaved.value;
});

watch(switchOn, (newValue, oldValue) => {
  if (
    oldValue === null ||
    newValue === oldValue ||
    newValue === config.value?.enabled
  ) {
    return;
  }
  newValue ? enableIntegration() : disableIntegration();
});

watch(
  () => config.value?.enabled,
  (newValue, oldValue) => {
    if (oldValue !== newValue) refreshTools();
  }
);

watch(
  () => config.value?.api_key,
  (newValue, oldValue) => {
    if (oldValue !== null && newValue === null) refreshModal();
    if (
      oldValue !== null &&
      oldValue !== newValue &&
      config.value?.verified_at
    ) {
      config.value.contact_first_name = null;
      config.value.contact_last_name = null;
      config.value.dealer_id = null;
      config.value.ein = null;
      config.value.state_license_number = null;
      config.value.verified_at = null;
    }
  }
);

function handleJB360Onboarded(data: Record<string, unknown>) {
  store.commit("replace", { state: "jbWarranties", data: { config: data } });
  switchOn.value = true;
}

onMounted(() => {
  store.dispatch("fetchJBWarranties").then(refreshTools);

  channel = new VueChannel();
  cable.subscribe(channel).catch((err: unknown) => {
    console.log("Unable to subscribe to VueChannel", err);
  });
  EventBus.$on("jb360-onboarded", handleJB360Onboarded);
});

onBeforeUnmount(() => {
  EventBus.$off("jb360-onboarded", handleJB360Onboarded);
  channel?.disconnect();
});

function inferSetupMode(): SetupMode {
  if (isDealerOnboardingEnabled.value || config.value?.jb360_enabled)
    return "dealer_id";
  return "api_key";
}

function openModal() {
  store.dispatch("fetchJBWarranties").then(() => {
    dirty.value = false;
    setupMode.value = inferSetupMode();
    modalOpen.value = true;
    refreshModal();
  });
}

function closeModal() {
  return store.dispatch("fetchJBWarranties").then(() => {
    modalOpen.value = false;
    refreshTools();
  });
}

function enableIntegration() {
  store
    .dispatch("changeJBWarranties", { enabled: true })
    .then(() => {
      window.notify.success("JB Warranties integration enabled!");
    })
    .catch(() => (switchOn.value = false));
}

function disableIntegration() {
  store.dispatch("changeJBWarranties", { enabled: false }).then(() => {
    window.notify.success("JB Warranties integration disabled!");
  });
}

async function validateApiKey() {
  try {
    validating.value = true;
    const result = await store.dispatch("validateJBWarranties", {
      api_key: config.value.api_key
    });
    Object.assign(config.value, result.config);
    dirty.value = true;
    window.notify.success("API Key validated successfully!");
  } catch {
    config.value.contact_first_name = null;
    config.value.contact_last_name = null;
    config.value.dealer_id = null;
    config.value.ein = null;
    config.value.state_license_number = null;
    config.value.verified_at = null;
  }
  validating.value = false;
}

async function saveIntegration() {
  try {
    saving.value = true;
    config.value.enabled = true;
    await store.dispatch("changeJBWarranties", config.value);
    dirty.value = false;
    switchOn.value = true;
    window.notify.success("JB Warranties settings saved and enabled!");
    closeModal();
  } catch {
    // form shows validation errors
  }
  saving.value = false;
}

function dropIntegration() {
  deleteConfirm.value = false;
  closeModal().then(() => {
    switchOn.value = false;
    if (config.value?.persisted) {
      store.dispatch("dropJBWarranties").then(() => {
        window.notify.success("JB Warranties settings deleted!");
      });
    } else {
      window.notify.error(
        "The integration has not been configured yet. There's nothing to delete."
      );
    }
  });
}

async function handleRequestParticipation(dealerId: string) {
  submitting.value = true;
  try {
    await store.dispatch("requestJB360Participation", {
      dealer_id: dealerId
    });
    window.notify.success("Dealer ID saved. Validating with JB360...");
  } catch {
    window.notify.error("Failed to save dealer ID. Please try again.");
  }
  submitting.value = false;
}

function refreshTools() {
  switchOn.value = config.value?.enabled ?? false;
  if (switchDisabled.value) switchOn.value = false;
}

function refreshModal() {
  if (!modalOpen.value) return;
  if (config.value?.api_key === "") config.value.api_key = null;
}

function unlockApiKey() {
  config.value.contact_first_name = null;
  config.value.contact_last_name = null;
  config.value.dealer_id = null;
  config.value.ein = null;
  config.value.state_license_number = null;
  config.value.verified_at = null;
  dirty.value = false;
}

function confirmDelete() {
  deleteConfirm.value = true;
}

function toggleDeleteConfirm() {
  deleteConfirm.value = !deleteConfirm.value;
}
</script>
